import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseLayout } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { kilobaApi, VoucherWithVisibility, notificationApi, Notification } from '../services/api';
import './NotificationPage.css';

interface Message {
  id: string;
  title: string;
  content: string;
  timestamp: Date;
  isRead: boolean;
  type: 'info' | 'reward' | 'system';
}

interface GiftHistory {
  id: string;
  giftName: string;
  giftType: 'coin' | 'energy' | 'voucher';
  voucherType: string;
  amount: number;
  timestamp: Date;
  source: string;
  code?: string | null;
  requiresProfileUpdate?: boolean;
  canBeUsed?: boolean;
  isExpired?: boolean;
}

const NotificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'inbox' | 'gifts'>('inbox');
  const [vouchers, setVouchers] = useState<VoucherWithVisibility[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [requiresProfileUpdate, setRequiresProfileUpdate] = useState(false);
  const [profileUpdateMessage, setProfileUpdateMessage] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Dữ liệu tạm thời cho demo messages
  // const mockMessages: Message[] = [];

  // Fetch notifications when component mounts or user changes
  useEffect(() => {
    if (user?.id && activeTab === 'inbox') {
      fetchNotifications();
    }
  }, [user?.id, activeTab]);

  const fetchNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await notificationApi.getNotifications({
        limit: 50,
        offset: 0
      });
      
      if (response.success && response.data) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user vouchers when component mounts or user changes
  useEffect(() => {
    if (user?.id && activeTab === 'gifts') {
      fetchUserVouchers();
    }
  }, [user?.id, activeTab]);

  const fetchUserVouchers = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await kilobaApi.getUserVouchersWithVisibility(user.id, 'claimed');
      
      if (response.success && response.data) {
        setVouchers(response.data.vouchers);
        setRequiresProfileUpdate(response.data.requiresProfileUpdate);
        setProfileUpdateMessage(response.data.profileUpdateMessage || null);
      }
    } catch (error) {
      console.error('Error fetching user vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert vouchers to gift history format
  const convertVouchersToGiftHistory = (): GiftHistory[] => {
    return vouchers.map(voucher => ({
      id: voucher.id,
      giftName: `Voucher ${voucher.voucher_type.toUpperCase()}`,
      voucherType: voucher.voucher_type,
      giftType: 'voucher' as const,
      amount: voucher.value,
      timestamp: new Date(voucher.claimed_at || voucher.created_at),
      source: 'Phần thưởng từ game',
      code: voucher.code,
      requiresProfileUpdate: voucher.requiresProfileUpdate,
      canBeUsed: voucher.canBeUsed,
      isExpired: voucher.isExpired
    }));
  };

  const handleBack = () => {
    navigate('/')
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.status === 'unread') {
      try {
        await notificationApi.markAsRead(notification.id);
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n.id === notification.id 
              ? { ...n, status: 'read' as const, readAt: new Date().toISOString() }
              : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'energy_gained':
        return '⚡';
      case 'kiloba_received':
        return '🐾';
      case 'kiloba_upgraded':
        return '⭐';
      case 'task_completed':
        return '✅';
      case 'voucher_received':
        return '🎫';
      case 'daily_checkin':
        return '📅';
      case 'game_reward':
        return '�';
      case 'system':
        return '⚙️';
      default:
        return '📢';
    }
  };

  const getGiftIcon = (type: string) => {
    switch (type) {
      case 'coin':
        return '🪙';
      case 'energy':
        return '⚡';
      case 'voucher':
        return '🎫';
      case 'item':
        return '🎁';
      default:
        return '🎁';
    }
  };

  const getVoucherBackgroundColor = (voucher: string): string => {
    console.log('Voucher type for color mapping:', voucher);
    switch (voucher) {
      case '10k':
        return '#A8E6A3';
      case '20k':
        return '#7DD3FC';
      case '50k':
        return '#C4B5FD';
      case '100k':
        return '#FDBA74';
      case '200k':
        return '#F87171';
      case '300k':
        return '#FB7185';
      case '500k':
        return '#0D9488';
      case '1m':
        return '#FACC15';
      case '2m':
        return '#F59E0B';
      default:
        return '#e8f5e8'; // Default green color
    }
  };

  const handleCopyVoucherCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      // Có thể thêm toast notification ở đây để thông báo copy thành công
      alert('Đã copy mã voucher!');
    } catch (error) {
      console.error('Failed to copy voucher code:', error);
      // Fallback cho trường hợp clipboard API không khả dụng
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Đã copy mã voucher!');
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        alert('Không thể copy mã voucher');
      }
      document.body.removeChild(textArea);
    }
  };

  const renderInboxTab = () => {
    return (
      <div className="notification-content">
        {loading ? (
          <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Đang tải...</p>
          </div>
        ) : (
          <div className="messages-list">
            {notifications.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p>Chưa có tin nhắn nào</p>
              </div>
            ) : (
              notifications.map((notification: Notification) => (
                <div 
                  key={notification.id} 
                  className={`message-item ${notification.status === 'unread' ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="message-content">
                    <div className="message-header">
                      <h3 className="message-title">{notification.title}</h3>
                      <span className="message-time">{formatDate(notification.createdAt)}</span>
                    </div>
                    <p className="message-text">{notification.content}</p>
                    <p className='message-created-at'>{new Date(notification.createdAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                    {notification.status === 'unread' && <div className="unread-dot"></div>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGiftsTab = () => {
    const giftHistory = convertVouchersToGiftHistory();
    
    return (
      <div className="notification-content">
        {/* {requiresProfileUpdate && profileUpdateMessage && (
          <div className="profile-update-notice" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '12px',
            margin: '16px 0',
            color: '#856404'
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>{profileUpdateMessage}</p>
          </div>
        )} */}
        
       
        
        {loading ? (
          <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Đang tải...</p>
          </div>
        ) : (
          <div className="gifts-list">
            {giftHistory.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎁</div>
                <p>Chưa có quà nào</p>
              </div>
            ) : (
              giftHistory.map((gift: GiftHistory) => (
                <div key={gift.id} className="gift-item" style={{
                  backgroundColor: getVoucherBackgroundColor(gift.voucherType),
                }}>
                  <div className="gift-content">
                    
                    
                    {/* Voucher code section */}
                    {gift.giftType === 'voucher' && (
                      <div className="voucher-info" style={{ 
                        marginTop: '8px',
                        }}>
                        {gift.code ? (
                          <div 
                            className="voucher-code" 
                            style={{
                              padding: '8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              border: '1px dashed rgba(0, 0, 0, 0.2)',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyVoucherCode(gift.code!);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';
                              e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Nhấn để copy mã voucher"
                          >
                            <strong style={{color: 'black'}}>Mã GotIT: {gift.code}</strong>
                            <span style={{ 
                              fontSize: '10px', 
                              color: '#666', 
                              marginLeft: '8px',
                              opacity: 0.7
                            }}>📋 Click để copy</span>
                            {gift.canBeUsed && (
                              <div style={{ color: '#28a745', marginTop: '4px' }}>
                                ✓ Có thể sử dụng
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="voucher-code-hidden" style={{
                            backgroundColor: '#f8f9fa',
                            padding: '8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#6c757d',
                            textAlign: 'start'
                          }}>
                            Mã Voucher GOT IT sẽ hiển thị sau khi tài khoản cập nhật thông tin cá nhân
                          </div>
                        )}
                        
                        {gift.isExpired && (
                          <div style={{ 
                            color: '#dc3545', 
                            fontSize: '12px', 
                            marginTop: '4px' 
                          }}>
                            ⚠️ Đã hết hạn
                          </div>
                        )}
                      </div>
                    )}

                    <div className="gift-header">
                      <h3 className="gift-name">{gift.giftName}</h3>
                      <span className="gift-time">{formatDate(gift.timestamp.toISOString())}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container notification-page">
        {/* Header */}
        <div className="notification-header-page safe-area-top">
          <button className="back-button-noti-page" onClick={handleBack}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="page-title-noti">Thông báo</h1>
          <div className="header-spacer"></div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <div 
            className={`tab-button ${activeTab === 'inbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('inbox')}
          >
            <span className="tab-icon">📥</span>
            <span className="tab-text">Hộp thư</span>
            {unreadCount > 0 && (
              <span className="tab-badge">{unreadCount}</span>
            )}
          </div>
          <div 
            className={`tab-button ${activeTab === 'gifts' ? 'active' : ''}`}
            onClick={() => setActiveTab('gifts')}
          >
            <span className="tab-icon">🎁</span>
            <span className="tab-text">Lịch sử nhận quà</span>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'inbox' ? renderInboxTab() : renderGiftsTab()}
        </div>
      </BaseLayout>
    </div>
  );
};

export default NotificationPage;